import React, { CSSProperties } from 'react';
import { Point, clamp, getDist } from '../coreui';

import globals from '../../globals';
import { fromEntries, getAngle, getFittingString } from '../../react_utils';
const { lineWidth, jointRadius, capWidth, capHeight, lineColor,
  highlightColor, borderThickness, borderColor, jointInsertDelay, areaOpacity } = globals.svg;

export type SVGMouseCallBack<TExtra extends any[]> = (e: React.MouseEvent<SVGElement, MouseEvent>, ...args: TExtra) => void;

export interface ISVGMouseCallbacks<TExtra extends any[]> {
  onMouseEnter?: SVGMouseCallBack<TExtra>;
  onMouseLeave?: SVGMouseCallBack<TExtra>;
  onMouseDown?: SVGMouseCallBack<TExtra>;
  onMouseUp?: SVGMouseCallBack<TExtra>;
  onMouseMove?: SVGMouseCallBack<TExtra>;
  onDoubleClick?: SVGMouseCallBack<TExtra>;
  onContextMenu?: SVGMouseCallBack<TExtra>;
}

export interface ICommonRenderProps {
  allowInteraction: boolean;
  highlight: boolean;
  grabbing?: boolean;
  scale: number;
}

export interface IRenderPathProps {
  closed: boolean;
  path: Point[] | string;
  lower: boolean;
  callbacks?: ISVGMouseCallbacks<[]>;
}

const getStyle = (allowInteraction: boolean, path: boolean, grabbing?: boolean): CSSProperties => allowInteraction ?
  { cursor: grabbing === undefined ? 'pointer' : (grabbing ? 'grabbing' : 'grab'), pointerEvents: path ? 'visibleStroke' : 'all' }
  : { pointerEvents: 'none' };

export const RenderPath: React.FC<IRenderPathProps & ICommonRenderProps> = ({
  closed, path, callbacks, lower, allowInteraction, highlight, grabbing, scale
}) => {
  const props: React.SVGAttributes<SVGPolylineElement | SVGPolygonElement> = {
    points: typeof path === 'string' ? path : path.map(p => `${p.x},${p.y}`).join(' '),
    strokeLinejoin: 'round',
    strokeWidth: `${(lineWidth + (lower ? borderThickness * 2 : 0)) * scale}px`,
    style: getStyle(lower && allowInteraction, true, grabbing),
    stroke: lower ? borderColor : highlight ? highlightColor : lineColor,
    ...callbacks
  };

  if (closed) {
    const fill = (closed && lower) ? (highlight ? highlightColor : borderColor).slice(0, 7) : 'none';
    return <polygon {...props} style={{...props.style, fill: fill, fillOpacity: areaOpacity}}/>
  } else {
    return <polyline {...props} style={{...props.style, fill: 'none'}}/>
  }
};

export interface IRenderJointProps {
  position: Point;
  lower: boolean;
  callbacks?: ISVGMouseCallbacks<[]>;
}

export interface IRenderCapProps {
  position: Point;
  lower: boolean;
  angle: number;
  callbacks?: ISVGMouseCallbacks<[]>;
}

const getCommonCapOrJointProps = (input: (IRenderJointProps | IRenderCapProps) & ICommonRenderProps) => {
  const { lower, highlight, allowInteraction, grabbing, callbacks } = input;
  const props: React.SVGAttributes<SVGCircleElement | SVGRectElement> = {
    fill: lower ? borderColor : (highlight ? highlightColor : lineColor),
    style: getStyle(lower && allowInteraction, false, grabbing),
    ...callbacks
  };
  return props;
};

export const RenderJoint: React.FC<IRenderJointProps & ICommonRenderProps> = (input) => {
  const r = `${(jointRadius + (input.lower ? borderThickness : 0)) * input.scale}px`;
  const { x, y } = input.position;

  return <circle cx={x} cy={y} r={r} {...getCommonCapOrJointProps(input)}/>
};

export const RenderCap: React.FC<IRenderCapProps & ICommonRenderProps> = (input) => {
  const adjust = input.lower ? borderThickness * input.scale : 0;
  const height = capHeight * input.scale + 2 * adjust;
  const props: React.SVGAttributes<SVGRectElement> = {
    ...getCommonCapOrJointProps(input),
    width: capWidth * input.scale + 2 * adjust,
    height,
    x: input.position.x - adjust,
    y: input.position.y - height / 2,
    transform: `rotate(${input.angle * 180 / Math.PI} ${input.position.x} ${input.position.y})`
  };

  return <rect {...props} />;
};


export interface IRenderPathCompleteProps {
  path: Point[];
  closed: boolean;
  allowPathGrab?: boolean;
  commonProps: ICommonRenderProps;
  pathCallbacks?: ISVGMouseCallbacks<[]>;
  jointCallbacks?: ISVGMouseCallbacks<[number]>;
}

export const RenderPathComplete: React.FC<IRenderPathCompleteProps> = ({
  path, closed, allowPathGrab, commonProps, pathCallbacks, jointCallbacks
}) => {
  const showEndCap = getDist(path[path.length - 2], path[path.length - 1]) > jointInsertDelay * commonProps.scale / (path.length === 2 ? 2 : 1);
  const lastIndex = showEndCap ? path.length - 1 : path.length - 2;
  const visiblePath = path.slice(0, lastIndex + 1);
  const pathString = visiblePath.map(({ x, y }) => `${x},${y}`).join(' ');

  const joints = (lower: boolean) => visiblePath.map((point, index) => {
    const callbacks = (lower && jointCallbacks) ? fromEntries(Object.keys(jointCallbacks).map(key => {
      const fun = jointCallbacks[key as keyof typeof jointCallbacks];
      if (fun)
        return [key, (e => fun(e, index)) as SVGMouseCallBack<[]>];
      return [key, undefined];
    })) as ISVGMouseCallbacks<[]> : undefined;

    const props = {
      lower,
      callbacks,
      position: point
    }

    if (closed || (index > 0 && index < lastIndex)) { // joint
      return <RenderJoint key={index} {...commonProps} {...props} />
    } else { // cap
      const angle = index === 0 ? getAngle(point, path[1]) : getAngle(point, path[index - 1]);
      return <RenderCap key={index} {...commonProps} {...props} angle={angle} />
    }
  });

  return (
    <>
      <g name='lower'>
        <RenderPath path={pathString} closed={closed} lower={true} callbacks={pathCallbacks} {...{ ...commonProps, grabbing: allowPathGrab ? commonProps.grabbing : undefined }} />
        {joints(true)}
      </g>
      <g name='upper'>
        <RenderPath path={pathString} closed={closed} lower={false} {...{ ...commonProps, allowInteraction: false }} />
        {joints(false)}
      </g>
    </>
  );
};

export interface ISVGTextProps {
  pos: Point;
  scale: number;
  space: number;
  angle: number;
  val: number;
  postfix?: string;
  below?: boolean;
  type: 'caps' | 'joints' | 'capjoint' | 'area' | 'index' | 'index-flipped';
}

export const RenderText: React.FC<ISVGTextProps> = ({ pos, scale, space, angle, val, postfix, below, type }) => {
  const getSpaces = () => {
    if (type === 'area')
      return { inner: { space: space / scale, off: 0 }, outer: { space: space / scale, off: 0 } };
    if (type === 'caps')
      return {
        inner: { space: space / scale - (capWidth + borderThickness), off: (lineWidth + borderThickness) / 2 },
        outer: { space: space / scale + (capWidth + borderThickness), off: (capHeight + borderThickness) / 2 }
      };
    if (type === 'joints')
      return {
        inner: { space: space / scale - (jointRadius + borderThickness) / 2, off: (lineWidth + borderThickness) / 2 },
        outer: { space, off: (jointRadius + borderThickness) / 2 }
      };
    if (type === 'capjoint')
      return {
        inner: { space: space / scale - (Math.max(capWidth, jointRadius) + borderThickness), off: (lineWidth + borderThickness) / 2 },
        outer: { space, off: (Math.max(capHeight, jointRadius) + borderThickness) / 2 }
      };
    return { inner: { space: 0, off: 0}, outer: { space: 0, off: 0} };
  };
  const { inner, outer } = getSpaces();

  const { overflow, text } = getFittingString(val, inner.space, postfix);
  const useSpace = overflow > 0 ? outer : inner;

  const shrink = type.startsWith('index') ? 1 : clamp(useSpace.space / (inner.space + overflow), 0, 1);

  const style: CSSProperties = {
    dominantBaseline: 'middle', textAnchor: 'middle', pointerEvents: 'none', fontFamily: 'Arial', fontWeight: 'bold', fontSize:`${30*scale*shrink}px`,
    stroke: 'black', strokeWidth: `${1.5 * scale * shrink}px`, strokeLinecap: 'butt', strokeLinejoin: 'miter', strokeOpacity: 1
  };

  const horOff = type === 'index' ? -9 * text.length : type === 'index-flipped' ? 9 * text.length : 0;
  const verOff = (below ? -1.3 : 1) *  (type.startsWith('index') ? 0 : (useSpace.off + 9 * shrink));

  return <text x={pos.x + horOff*scale} y={pos.y - verOff * scale}
    fill='white'
    transform={`rotate(${angle}, ${pos.x}, ${pos.y})`} style={style} >
    {text}
  </text>;
};